import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAnimatedNavigate } from '../../../hooks/useAnimatedNavigate';
import { useKeycloak } from '@react-keycloak/web';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';

interface AcceptanceResult {
  success: boolean;
  message: string;
  collaborator_id?: string;
}

/**
 * CollaborationAcceptPage
 * 
 * Handles invitation acceptance when users click email invitation links.
 * URL format: /collaboration/accept?token=<invitation_token>
 */
export const CollaborationAcceptPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useAnimatedNavigate();
  const { keycloak, initialized } = useKeycloak();
  
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [result, setResult] = useState<AcceptanceResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!initialized) return;

    // Redirect to login if not authenticated
    if (!keycloak.authenticated) {
      console.log('[CollaborationAccept] User not authenticated, redirecting to login');
      keycloak.login({
        redirectUri: window.location.href // Return to this page after login
      });
      return;
    }

    // Validate token parameter
    if (!token) {
      setStatus('error');
      setErrorMessage('Invalid invitation link: missing token parameter');
      return;
    }

    // Accept the invitation
    acceptInvitation(token);
  }, [initialized, keycloak.authenticated, token]);

  const acceptInvitation = async (invitationToken: string) => {
    try {
      console.log(`[CollaborationAccept] Accepting invitation with token: ${invitationToken.substring(0, 8)}...`);
      
      const response = await fetch(`/api/v1/invitations/${invitationToken}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('[CollaborationAccept] Invitation accepted successfully:', data);
        setResult(data);
        setStatus('success');
      } else {
        console.error('[CollaborationAccept] Failed to accept invitation:', data);
        setStatus('error');
        setErrorMessage(data.message || 'Failed to accept invitation');
      }
    } catch (error) {
      console.error('[CollaborationAccept] Error accepting invitation:', error);
      setStatus('error');
      setErrorMessage('Network error occurred while accepting invitation');
    }
  };

  const handleContinue = () => {
    // Navigate to documents dashboard
    navigate('/documents');
  };

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-lex-accent" />
          <p className="text-gray-600">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          {/* EdgeLex Logo/Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">EdgeLex</h1>
            <p className="text-gray-600">Document Collaboration</p>
          </div>

          {/* Loading State */}
          {status === 'loading' && (
            <div>
              <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-lex-accent" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Processing Invitation
              </h2>
              <p className="text-gray-600">
                Please wait while we add you to the document collaboration...
              </p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && result && (
            <div>
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-600" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Invitation Accepted!
              </h2>
              <p className="text-gray-600 mb-6">
                {result.message}
              </p>
              <button
                onClick={handleContinue}
                className="inline-flex items-center px-6 py-3 bg-lex-accent text-lex-accent-contrast font-medium rounded-lg hover:bg-lex-accent-hover transition-colors"
              >
                Continue to Documents
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div>
              <XCircle className="h-12 w-12 mx-auto mb-4 text-red-600" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Unable to Accept Invitation
              </h2>
              <p className="text-gray-600 mb-6">
                {errorMessage}
              </p>
              <div className="space-y-3">
                <button
                  onClick={handleContinue}
                  className="w-full px-6 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Continue to Documents
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="w-full px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Need help? Contact your document administrator.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};





